import { z } from 'zod';
import { PublicCheckoutStatusSchema, PublicExperienceStatusSchema, PublicExperienceTemplateSchema, PublicExperienceTypeSchema, SubmissionWorkflowStatusSchema, SubscriptionCancelModeSchema, } from './enums';
import { AdminExperienceSummarySchema, ExperienceBrandingSchema, ExperienceDescriptionSchema, ExperienceDraftSchema, ExperienceTitleSchema, ExperienceFormClassificationShape, } from './experience';
import { OfferDraftSchema, PublicOfferViewSchema } from './offer';
import { AdminSubmissionSummarySchema, SubmissionAcceptanceSchema, } from './submission';
import { ContactEmailSchema, PublicExperienceContactSchema } from './contact';
import { LiveRecurringSummarySchema } from './billing';
import { CancelSubscriptionRequestSchema, CancelSubscriptionResponseSchema, CreateAdminExperienceRequestSchema, DisableAdminExperienceRequestSchema, ListAdminExperiencesQuerySchema, PaginationQuerySchema, PublishAdminExperienceRequestSchema, UpdateAdminExperienceRequestSchema, createPaginatedResponseSchema, } from './api-admin';
import { CursorQuerySchema, PayformCustomerDetailSchema, PayformCustomerSummarySchema, PayformInvoiceRowSchema, PayformPaymentRowSchema, PayformRecurringAgreementSchema, createCursorPageSchema, } from './customers';
import { AccountNotDirectEligibleReasonSchema } from './errors';
import { PaymentMethodDomainEligibilitySchema } from './payment-domains';
import { CheckoutIdSchema, ExperienceIdSchema, HttpsUrlSchema, IsoTimestampSchema, PublicExperiencePublicIdSchema, PublishedRevisionSchema, StripeCustomerIdSchema, StripeSubscriptionIdSchema, SubmissionIdSchema, } from './primitives';
/**
 * Public Experiences — merchant self-service API (`/api/business/payforms/**`).
 *
 * Authorization: the EXISTING business chain — a Firebase ID token plus the
 * business account resolved server-side from `request.businessAccount.id`
 * (super-admins may impersonate through the existing `X-BUSINESS-ID`
 * mechanism). There is therefore NO `:businessId` path parameter on any route:
 * the business is derived, never addressed. Per-route permissions (PF_READ,
 * PF_MANAGE, PF_PII, PF_CANCEL) are defined in PLAN.md §7 (DEC-P2-03).
 *
 * These wrappers call the same services as `/api/admin/public-experiences/**`
 * but return MERCHANT DTOs: no `applicationFeePercent`, no
 * `expectedPlatformFeeCents`, no provider catalog ids (`productId`, `priceId`),
 * no account ids and no execution scope. Resource identities the merchant
 * addresses (`customerId`, `subscriptionId`, `paymentIntentId`, `invoiceId`)
 * are, by construction, part of the DTOs.
 *
 * Responses use the backend's existing `AdminResultSchema` /
 * `createAdminResponse` envelope (`{ result, validationErrors: [] }`).
 */
export const BUSINESS_PAYFORM_ROUTE_PREFIX = '/api/business/payforms';
/**
 * Route patterns. `customers` is a static segment under the same prefix as
 * `:experienceId`; Fastify's router prefers static segments, and experience ids
 * are server-generated `pce_…` ids, so the two never collide.
 */
export const BUSINESS_PAYFORM_ROUTES = {
    /** GET (list) and POST (create draft). */
    collection: '/api/business/payforms',
    /** GET (detail) and PUT (update draft). */
    item: '/api/business/payforms/:experienceId',
    publish: '/api/business/payforms/:experienceId/publish',
    disable: '/api/business/payforms/:experienceId/disable',
    eligibility: '/api/business/payforms/:experienceId/eligibility',
    submissions: '/api/business/payforms/:experienceId/submissions',
    submissionItem: '/api/business/payforms/:experienceId/submissions/:submissionId',
    submissionHistory: '/api/business/payforms/:experienceId/submissions/:submissionId/history',
    cancelSubscription: '/api/business/payforms/:experienceId/submissions/:submissionId/cancel-subscription',
    customers: '/api/business/payforms/customers',
    customerSearch: '/api/business/payforms/customers/search',
    customerItem: '/api/business/payforms/customers/:customerId',
    customerPayments: '/api/business/payforms/customers/:customerId/payments',
    customerRecurring: '/api/business/payforms/customers/:customerId/recurring',
    customerRecurringCancel: '/api/business/payforms/customers/:customerId/recurring/:subscriptionId/cancel',
    /**
     * GET — the payment-method domain component for the caller's linked account
     * (capability `PAYMENT_SETUP`; DEC-P2-18).
     */
    paymentDomains: '/api/business/payforms/payment-domains',
    /**
     * POST `{ confirm: true }` — register / re-enable the required domains on the
     * caller's linked account (capability `PAYMENT_SETUP`; 10 per minute).
     */
    paymentDomainsEnsure: '/api/business/payforms/payment-domains/ensure',
};
/** Static first segments under the prefix that an `:experienceId` must never equal. */
export const RESERVED_BUSINESS_PAYFORM_SEGMENTS = [
    'customers',
    'payment-domains',
];
export const buildBusinessPaymentDomainsPath = () => BUSINESS_PAYFORM_ROUTES.paymentDomains;
export const buildBusinessPaymentDomainsEnsurePath = () => BUSINESS_PAYFORM_ROUTES.paymentDomainsEnsure;
// ---------------------------------------------------------------------------
// Params and queries
// ---------------------------------------------------------------------------
export const BusinessPayformParamsSchema = z
    .object({ experienceId: ExperienceIdSchema })
    .strict();
export const BusinessPayformSubmissionParamsSchema = z
    .object({
    experienceId: ExperienceIdSchema,
    submissionId: SubmissionIdSchema,
})
    .strict();
export const BusinessPayformCustomerParamsSchema = z
    .object({ customerId: StripeCustomerIdSchema })
    .strict();
export const BusinessPayformRecurringParamsSchema = z
    .object({
    customerId: StripeCustomerIdSchema,
    subscriptionId: StripeSubscriptionIdSchema,
})
    .strict();
/** Firestore-backed lists keep the existing `page`/`limit` pagination. */
export const ListMerchantExperiencesQuerySchema = ListAdminExperiencesQuerySchema;
export const ListMerchantSubmissionsQuerySchema = PaginationQuerySchema;
/** `GET /customers?cursor&limit&email` — exact (normalized) email filter. */
export const ListPayformCustomersQuerySchema = CursorQuerySchema.extend({
    email: ContactEmailSchema.optional(),
});
/**
 * `GET /customers/search?q&cursor&limit`. The server builds and escapes the
 * provider query (`name~'…' OR email~'…'`); the client sends plain text only.
 * Results are eventually consistent — the UI labels them so.
 */
export const PAYFORM_CUSTOMER_SEARCH_MIN_LENGTH = 2;
export const PAYFORM_CUSTOMER_SEARCH_MAX_LENGTH = 100;
export const SearchPayformCustomersQuerySchema = CursorQuerySchema.extend({
    q: z
        .string()
        .trim()
        .min(PAYFORM_CUSTOMER_SEARCH_MIN_LENGTH)
        .max(PAYFORM_CUSTOMER_SEARCH_MAX_LENGTH),
});
// ---------------------------------------------------------------------------
// Requests (reused from the admin API — same validation, same refinements)
// ---------------------------------------------------------------------------
export const CreateMerchantExperienceRequestSchema = CreateAdminExperienceRequestSchema;
export const UpdateMerchantExperienceRequestSchema = UpdateAdminExperienceRequestSchema;
export const PublishMerchantExperienceRequestSchema = PublishAdminExperienceRequestSchema;
export const DisableMerchantExperienceRequestSchema = DisableAdminExperienceRequestSchema;
/** Same confirmation rule as the admin cancel; `mode` defaults to AT_PERIOD_END. */
export const MerchantCancelSubscriptionRequestSchema = CancelSubscriptionRequestSchema;
// ---------------------------------------------------------------------------
// Experience DTOs (no fee or provider internals)
// ---------------------------------------------------------------------------
/** List row — the admin row already carries no fee or provider field. */
export const MerchantExperienceSummarySchema = AdminExperienceSummarySchema;
/**
 * The live snapshot as a merchant sees it: the published draft fields only.
 * `offer` is the plain `OfferDraftSchema` — no `applicationFeePercent`,
 * `expectedPlatformFeeCents` or `provider` block.
 */
export const MerchantPublishedSnapshotSchema = z
    .object({
    revision: PublishedRevisionSchema,
    publishedAt: IsoTimestampSchema,
    title: ExperienceTitleSchema,
    description: ExperienceDescriptionSchema,
    branding: ExperienceBrandingSchema,
    offer: OfferDraftSchema,
})
    .strict();
/**
 * Merchant detail = admin detail WITHOUT `businessId` (derived), `updatedBy`
 * (internal uid), fee fields and provider ids. `requiresRepublish` is true when
 * the live snapshot was published before direct charges (v1 linkage): new
 * checkouts are refused with `PXE-016` until the merchant republishes.
 */
export const MerchantExperienceDetailSchema = z
    .object({
    ...ExperienceFormClassificationShape,
    experienceId: ExperienceIdSchema,
    publicId: PublicExperiencePublicIdSchema,
    type: PublicExperienceTypeSchema,
    template: PublicExperienceTemplateSchema,
    status: PublicExperienceStatusSchema,
    draft: ExperienceDraftSchema,
    published: MerchantPublishedSnapshotSchema.nullable(),
    /** Server-rendered preview of the DRAFT offer in the public shape. */
    previewOffer: PublicOfferViewSchema,
    publicUrl: HttpsUrlSchema,
    hasUnpublishedChanges: z.boolean(),
    requiresRepublish: z.boolean(),
    createdAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema,
})
    .strict();
export const ListMerchantExperiencesResponseSchema = createPaginatedResponseSchema(MerchantExperienceSummarySchema).extend({
    unclassified: z.array(MerchantExperienceSummarySchema).optional(),
});
export const MerchantExperienceResponseSchema = MerchantExperienceDetailSchema;
/**
 * `GET …/:experienceId/eligibility` — direct-charge readiness as a STATE plus
 * a reason. No processing rate, no fee percent, no account id.
 */
export var MerchantEligibilityStateEnum;
(function (MerchantEligibilityStateEnum) {
    MerchantEligibilityStateEnum["ELIGIBLE"] = "ELIGIBLE";
    MerchantEligibilityStateEnum["NOT_ELIGIBLE"] = "NOT_ELIGIBLE";
    /** The provider could not be reached; not a verdict. */
    MerchantEligibilityStateEnum["UNAVAILABLE"] = "UNAVAILABLE";
})(MerchantEligibilityStateEnum || (MerchantEligibilityStateEnum = {}));
export const MerchantEligibilityStateSchema = z.nativeEnum(MerchantEligibilityStateEnum);
export const MerchantEligibilityResponseSchema = z
    .object({
    state: MerchantEligibilityStateSchema,
    /** Present exactly when `state` is NOT_ELIGIBLE. */
    reason: AccountNotDirectEligibleReasonSchema.nullable(),
    checkedAt: IsoTimestampSchema,
    /**
     * Payment-method domain component (DEC-P2-18). Absent from responses
     * written before it existed and when the verdict stopped earlier
     * (NOT_LINKED, DIRECT_DISABLED, CHARGES_DISABLED). Hostnames only.
     */
    paymentDomains: PaymentMethodDomainEligibilitySchema.optional(),
})
    .strict()
    .superRefine((response, ctx) => {
    const needsReason = response.state === MerchantEligibilityStateEnum.NOT_ELIGIBLE;
    if (needsReason !== (response.reason !== null)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['reason'],
            message: 'reason is required exactly when state is NOT_ELIGIBLE',
        });
    }
});
// ---------------------------------------------------------------------------
// Submission DTOs
// ---------------------------------------------------------------------------
/** List row — identical to the admin row (contact data, no provider ids). */
export const MerchantSubmissionSummarySchema = AdminSubmissionSummarySchema;
export const ListMerchantSubmissionsResponseSchema = createPaginatedResponseSchema(MerchantSubmissionSummarySchema);
/**
 * Submission detail for a merchant: contact + acceptance + ONE live billing
 * read. No `businessId`, no provider linkage block. `manageable` says whether
 * `cancel-subscription` is allowed (PointCraft-created in the current scope).
 */
export const MerchantSubmissionDetailSchema = z
    .object({
    submissionId: SubmissionIdSchema,
    experienceId: ExperienceIdSchema,
    checkoutId: CheckoutIdSchema,
    contact: PublicExperienceContactSchema,
    accepted: SubmissionAcceptanceSchema,
    workflowStatus: SubmissionWorkflowStatusSchema,
    checkoutStatus: PublicCheckoutStatusSchema,
    confirmedAt: IsoTimestampSchema.nullable(),
    billing: LiveRecurringSummarySchema,
    manageable: z.boolean(),
    createdAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema,
})
    .strict();
export const MerchantSubmissionResponseSchema = MerchantSubmissionDetailSchema;
/** Submission-addressed cancel: same response as the admin route. */
export const MerchantCancelSubscriptionResponseSchema = CancelSubscriptionResponseSchema;
// ---------------------------------------------------------------------------
// Customer directory responses
// ---------------------------------------------------------------------------
export const ListPayformCustomersResponseSchema = createCursorPageSchema(PayformCustomerSummarySchema);
/** Same envelope as the list; results are eventually consistent. */
export const SearchPayformCustomersResponseSchema = createCursorPageSchema(PayformCustomerSummarySchema);
export const PAYFORM_CUSTOMER_SEARCH_CONSISTENCY = 'EVENTUAL';
export const PayformCustomerResponseSchema = PayformCustomerDetailSchema;
export const PayformCustomerPaymentsResponseSchema = createCursorPageSchema(PayformPaymentRowSchema);
/**
 * `GET …/customers/:customerId/recurring` — every subscription for the
 * Customer (`status: 'all'`, canceled included) plus the Customer's invoices,
 * listed separately from payments. Not paginated: bounded by the provider's
 * per-customer counts.
 */
export const PayformCustomerRecurringResponseSchema = z
    .object({
    customerId: StripeCustomerIdSchema,
    agreements: z.array(PayformRecurringAgreementSchema).max(100),
    invoices: z.array(PayformInvoiceRowSchema).max(100),
    readAt: IsoTimestampSchema,
})
    .strict();
/**
 * `POST …/customers/:customerId/recurring/:subscriptionId/cancel` — allowed
 * only when the agreement is `manageable` (else `PXE-019`). Never issues a
 * refund; returns the refreshed agreement.
 */
export const MerchantCancelRecurringResponseSchema = z
    .object({
    customerId: StripeCustomerIdSchema,
    subscriptionId: StripeSubscriptionIdSchema,
    mode: SubscriptionCancelModeSchema,
    cancelAtPeriodEnd: z.boolean(),
    canceledAt: IsoTimestampSchema.nullable(),
    effectiveAt: IsoTimestampSchema.nullable(),
    refundIssued: z.literal(false),
    agreement: PayformRecurringAgreementSchema,
})
    .strict();
